import type { RxCollection, RxDocument, RxJsonSchema } from "rxdb"
import { getReplicationProperties } from "../replication/getReplicationProperties"
import type { CollectionDocType } from "@oboku/shared"
import { generateId, migrateResourceIdToData } from "./utils"

// biome-ignore lint/complexity/noBannedTypes: TODO
type CollectionDocMethods = {}

type CollectionDocument = RxDocument<CollectionDocType, CollectionDocMethods>

type CollectionCollectionMethods = {
  post: (
    json: Omit<CollectionDocType, "_id" | "rx_model" | "_rev" | `rxdbMeta`>,
  ) => Promise<CollectionDocument>
}

export type CollectionCollection = RxCollection<
  CollectionDocType,
  CollectionDocMethods,
  CollectionCollectionMethods
>

type DeprecatedProps = {
  name?: string
  resourceId?: string
  dataSourceId?: string
}

export const collectionSchema: RxJsonSchema<
  Omit<CollectionDocType & DeprecatedProps, `_rev` | `rxdbMeta`>
> = {
  title: "obokucollection",
  version: 2,
  type: "object",
  primaryKey: `_id`,
  properties: {
    _id: { type: `string`, maxLength: 100 },
    books: { type: "array", ref: "book", items: { type: "string" } },
    resourceId: { type: ["string", "null"] },
    name: { type: ["string", "null"] },
    type: { type: ["string", "null"] },
    linkType: { type: "string" },
    linkData: { type: "object" },
    createdAt: { type: "string" },
    modifiedAt: { type: ["string", "null"] },
    lastMetadataUpdatedAt: { type: ["string"] },
    lastMetadataStartedAt: { type: ["string"] },
    lastMetadataUpdateError: { type: ["string", "null"] },
    metadataUpdateStatus: { type: ["string"] },
    syncAt: { type: ["string"] },
    dataSourceId: { type: ["string", "null"] },
    metadata: { type: ["array"] },
    metadataFetchEnabled: { type: ["boolean", "null"] },
    ...getReplicationProperties(`obokucollection`),
  },
}

export function migrateLinkResourceIdToLinkData(
  linkType: string | undefined,
  linkResourceId: string | undefined,
  existingLinkData: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (!linkResourceId || !linkType) return existingLinkData

  return migrateResourceIdToData(linkType, linkResourceId, existingLinkData)
}

export const collectionMigrationStrategies = {
  1: (oldDoc: Record<string, unknown>) => {
    const linkType = oldDoc.linkType as string | undefined
    const linkResourceId = oldDoc.linkResourceId as string | undefined
    const existingLinkData = oldDoc.linkData as
      | Record<string, unknown>
      | undefined

    const newLinkData = migrateLinkResourceIdToLinkData(
      linkType,
      linkResourceId,
      existingLinkData,
    )

    const { linkResourceId: _, ...rest } = oldDoc

    return {
      ...rest,
      ...(newLinkData !== undefined && { linkData: newLinkData }),
    }
  },
  2: (oldDoc: Record<string, unknown>) => oldDoc,
}

export const collectionCollectionMethods: CollectionCollectionMethods = {
  post: async function (this: CollectionCollection, json) {
    return this.insert({ _id: generateId(), ...json } as CollectionDocType)
  },
}
